import { useAuthStore } from "@/stores/auth";
import {
  BASE_URL,
  AD_APP_AUTHORITY,
  AD_APP_TENANT,
  AD_APP_CLIENTID,
  API_AD_APP_SCOPE,
} from "../helpers/config";
import * as msal from "@azure/msal-browser";

const msalInstance = new msal.PublicClientApplication({
  auth: {
    clientId: AD_APP_CLIENTID ?? "",
    authority: `${AD_APP_AUTHORITY}${AD_APP_TENANT}`,
    redirectUri: `${window.location.origin}${BASE_URL}`,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: true,
    secureCookies: true,
  },
  system: {
    windowHashTimeout: 60000,
    iframeHashTimeout: 6000,
    loadFrameTimeout: 0,
    asyncPopups: false,
  },
  telemetry: {
    application: {
      appName: "Terminal Digitisation",
      appVersion: "3.6.0",
    },
  },
});

const loginRequest = {
  scopes: ["User.Read", "profile", "openid", "offline_access"],
};

/**
 * A set of helper methods which wrap around the main msal auth library to
 * login with Entra ID
 */
export function useAuthentication() {
  const authStore = useAuthStore();

  async function initializeAuthentication() {
    await msalInstance.initialize();
  }

  function signIn() {
    selectAccount();
  }

  function signOut() {
    const logoutRequest = {
      account: msalInstance.getActiveAccount(),
    };

    msalInstance.logoutRedirect(logoutRequest);
  }

  async function authenticate() {
    if (API_AD_APP_SCOPE == null) {
      throw new Error("App ID must be specified in config");
    }
    await getTokenRedirect(API_AD_APP_SCOPE)
      .then(handleResponse)
      .catch((error) => {
        throw error;
      });
  }

  async function getTokenRedirect(scope: string) {
    const accessTokenRequest = {
      scopes: [scope],
    };

    await msalInstance
      .handleRedirectPromise()
      .then(selectAccount)
      .catch((error) => {
        throw error;
      });

    return msalInstance
      .acquireTokenSilent(accessTokenRequest)
      .catch((error) => {
        if (error instanceof msal.InteractionRequiredAuthError) {
          msalInstance
            .acquireTokenRedirect(accessTokenRequest)
            .catch((error) => {
              throw error;
            });
        }
        throw error;
      });
  }

  function handleResponse(response: msal.AuthenticationResult | null) {
    if (response?.account != null) {
      const account = response.account;
      const accessToken = response.accessToken;
      msalInstance.setActiveAccount(account);

      const authStore = useAuthStore();
      const setTokenInfo = (accessToken) => authStore.setTokenInfo(accessToken);
      authStore.user = account;
      setTokenInfo(accessToken);
      return;
    }

    selectAccount();
  }

  function selectAccount() {
    const currentAccounts = msalInstance.getAllAccounts();

    if (currentAccounts.length === 0) {
      msalInstance.loginRedirect(loginRequest);
    } else if (currentAccounts.length > 1) {
      // Add your account choosing logic here
      console.warn("Multiple accounts detected.");
    } else if (currentAccounts.length === 1) {
      const account = msalInstance.getAllAccounts()[0];
      msalInstance.setActiveAccount(account);

      authStore.user = account;
    }
  }

  function isInRole(roleNames: string[]): boolean {
    let found = false;
    roleNames.forEach((element) => {
      if (authStore.roles.includes(element)) {
        found = true;
      }
    });
    return found;
  }

  return {
    initializeAuthentication,
    authenticate,
    getTokenRedirect,
    signIn,
    signOut,
    isInRole,
  };
}
